import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ntd, dt, STAGE_LABEL } from '@/lib/format';
import { Empty, Stat, Tag, cellStyle, thStyle } from '@/components/ui';

interface Profile {
  ownerName: string | null;
  wallet: { balanceCents: number; depositedCents: number };
  tickets: {
    id: string;
    treatmentName: string;
    remaining: number;
    total: number;
    expireAt: string | null;
  }[];
  stats: {
    visits: number;
    upcomingCount: number;
    nextAppointmentAt: string | null;
    lifetimeValueCents: number;
    balanceCents: number;
    activeTicketCount: number;
    noShowCount: number;
    firstVisitAt: string | null;
    lastVisitAt: string | null;
  };
  history: {
    id: string;
    startAt: string;
    stage: string;
    treatmentName: string;
    doctorName: string;
  }[];
}

const stageTone = (s: string): Parameters<typeof Tag>[0]['tone'] =>
  s === 'DONE' ? 'success' : s === 'CANCELLED' ? 'danger' : s === 'BOOKED' ? 'default' : 'warn';

/** 顧客 360:經營指標 + 持有(儲值/堂票)+ 到訪史,一頁看完。 */
export function CustomerProfile({ customerId }: { customerId: string }) {
  const q = useQuery({
    queryKey: ['customer-profile', customerId],
    queryFn: () => api.get<Profile>(`/customers/${customerId}/profile`),
    enabled: !!customerId,
  });

  if (q.isLoading) return <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>載入中…</div>;
  if (!q.data) return <Empty>無法載入顧客資料。</Empty>;
  const { stats, wallet, tickets, history, ownerName } = q.data;

  return (
    <div>
      {/* 經營指標 */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <Stat label="累計消費 (LTV)" value={ntd(stats.lifetimeValueCents)} tone="primary" hint="累計儲值" />
        <Stat label="到訪次數" value={stats.visits} hint="已完成療程" />
        <Stat label="儲值餘額" value={ntd(wallet.balanceCents)} tone="wallet" />
        <Stat label="可用堂票" value={`${stats.activeTicketCount} 堂`} tone="ticket" />
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat
          label="下次預約"
          value={stats.nextAppointmentAt ? dt(stats.nextAppointmentAt) : '無'}
          tone={stats.nextAppointmentAt ? 'success' : 'default'}
          hint={`未來 ${stats.upcomingCount} 筆`}
        />
        <Stat label="爽約次數" value={stats.noShowCount} tone={stats.noShowCount >= 2 ? 'danger' : 'default'} hint={stats.noShowCount >= 2 ? '需訂金保護' : ''} />
        <Stat label="主責" value={ownerName ?? '未指派'} />
      </div>

      {/* 持有堂票 */}
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
        持有堂票（{tickets.length} 種）
      </div>
      {tickets.length === 0 ? (
        <Empty>無可用堂票。</Empty>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 18 }}>
          <thead>
            <tr>
              <th style={thStyle}>療程</th>
              <th style={thStyle}>剩餘 / 總堂數</th>
              <th style={thStyle}>到期</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.id}>
                <td style={cellStyle}>{t.treatmentName}</td>
                <td style={cellStyle} className="num">
                  <Tag tone={t.remaining <= 1 ? 'warn' : 'ticket'}>
                    {t.remaining} / {t.total}
                  </Tag>
                </td>
                <td style={cellStyle} className="num">
                  {t.expireAt ? new Date(t.expireAt).toLocaleDateString('zh-TW') : '無期限'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* 到訪史 */}
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
        到訪歷史（近 {history.length} 筆）
      </div>
      {history.length === 0 ? (
        <Empty>尚無預約 / 到訪紀錄。</Empty>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>時間</th>
              <th style={thStyle}>療程</th>
              <th style={thStyle}>醫師</th>
              <th style={thStyle}>狀態</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => (
              <tr key={h.id}>
                <td style={cellStyle} className="num">{dt(h.startAt)}</td>
                <td style={cellStyle}>{h.treatmentName}</td>
                <td style={cellStyle}>{h.doctorName}</td>
                <td style={cellStyle}>
                  <Tag tone={stageTone(h.stage)}>{STAGE_LABEL[h.stage] ?? h.stage}</Tag>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
